import { lazy } from 'react'
import { redirect } from 'react-router-dom'

import { SoftwareManagementGetSoftwareSetupDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

import { ApplicationHeader } from 'modules/software_management/components/ApplicationDetailsContent/ApplicationHeader'
import { ApplicationEmployees } from 'modules/software_management/components/ApplicationDetailsContent/Tabs/Employees'
import { Overview } from 'modules/software_management/components/ApplicationDetailsContent/Tabs/Overview'
import { Spends } from 'modules/software_management/components/ApplicationDetailsContent/Tabs/Spends'
import { getSoftware } from 'modules/software_management/lib/software'

const RootPage = lazy(() => import('modules/software_management/pages/RootPage'))

const AccountSetupPage = lazy(() => import('modules/software_management/pages/AccountSetupPage'))
const ApplicationDetailsPage = lazy(
  () => import('modules/software_management/pages/ApplicationDetailsPage')
)
const ApplicationsListPage = lazy(
  () => import('modules/software_management/pages/ApplicationsListPage')
)
const ApplicationsPage = lazy(() => import('modules/software_management/pages/ApplicationsPage'))
const EmployeeSoftwareDetailsPage = lazy(
  () => import('modules/software_management/pages/EmployeeSoftwareDetailsPage')
)
const EmployeeSoftwareListPage = lazy(
  () => import('modules/software_management/pages/EmployeeSoftwareListPage')
)
const EmployeeSoftwarePage = lazy(
  () => import('modules/software_management/pages/EmployeeSoftwarePage')
)
const PaymentDetailsPage = lazy(
  () => import('modules/software_management/pages/PaymentDetailsPage')
)
const TerminatedEmployeesPage = lazy(
  () => import('modules/software_management/pages/TerminatedEmployeesPage')
)

const softwareManagementSubModuleRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.finance.spending.software.path,
      element: <RootPage />,
      handle: {
        meta: () => {
          return {
            title: i18n.t('software_management.navbar.software'),
            subNavigation: [
              {
                index: true,
                title: i18n.t('software_management.navbar.applications'),
                href: navigation.finance.spending.software.applications.fullPath(),
                isVisible: true,
              },
              {
                title: i18n.t('software_management.navbar.employees'),
                href: navigation.finance.spending.software.employees.fullPath(),
                isVisible: true,
              },
            ],
          }
        },
      },
      children: [
        {
          index: true,
          loader: () => redirect(navigation.finance.spending.software.applications.fullPath()),
        },
        {
          path: navigation.finance.spending.software.applications.path,
          element: <ApplicationsPage />,
          handle: {
            meta: () => ({
              title: i18n.t('software_management.navbar.applications'),
              description: i18n.t('software_management.software_setups.list.header.description'),
              illustration: 'sync',
            }),
          },
          children: [
            {
              index: true,
              element: <ApplicationsListPage />,
            },
            {
              path: navigation.finance.spending.software.applications.account_setup.path,
              element: <AccountSetupPage />,
              handle: {
                resource: true,
                meta: () => {
                  return { title: i18n.t('software_management.navbar.account_setup') }
                },
              },
            },
            {
              path: navigation.finance.spending.software.applications.applicationId.path,
              element: <ApplicationDetailsPage />,
              handle: {
                resource: true,
                meta: ({ params, watchQuery }) =>
                  watchQuery({
                    query: SoftwareManagementGetSoftwareSetupDocument,
                    fetchPolicy: 'cache-and-network',
                    variables: {
                      id: String(params['application_id']),
                    },
                  }).map(({ data }) => {
                    const applicationId = String(params['application_id'])
                    const { softwareSetup } = data?.softwareManagement || {}
                    if (!softwareSetup) {
                      return { title: applicationId.toString() }
                    }
                    const software = getSoftware(softwareSetup)
                    return {
                      headerComponent: <ApplicationHeader softwareSetup={softwareSetup} />,
                      title: software.name,
                      subNavigation: [
                        {
                          title: i18n.t('software_management.software_setups.tabs.overview.title'),
                          href: navigation.software.applications.applicationId.overview.fullPath({
                            application_id: applicationId,
                          }),
                          index: true,
                        },
                        {
                          title: i18n.t('software_management.software_setups.tabs.employees.title'),
                          href: navigation.software.applications.applicationId.employees.fullPath({
                            application_id: applicationId,
                          }),
                        },
                        {
                          title: i18n.t('software_management.software_setups.tabs.spends.title'),
                          href: navigation.software.applications.applicationId.spends.fullPath({
                            application_id: applicationId,
                          }),
                        },
                      ],
                    }
                  }),
              },
              children: [
                {
                  path: navigation.software.applications.applicationId.overview.path,
                  element: <Overview />,
                  index: true,
                },
                {
                  path: navigation.software.applications.applicationId.spends.path,
                  element: <Spends />,
                },
                {
                  path: navigation.software.applications.applicationId.employees.path,
                  element: <ApplicationEmployees />,
                },
                {
                  path: navigation.software.applications.applicationId.paymentId.path,
                  element: <PaymentDetailsPage />,
                },
              ],
            },
            {
              path: navigation.finance.spending.software.applications.terminatedEmployees.path,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('software_management.terminated_employees.title'),
                  }
                },
              },
              children: [
                {
                  index: true,
                  element: <TerminatedEmployeesPage />,
                },
                {
                  path: navigation.finance.spending.software.applications.terminatedEmployees
                    .employeeId.path,
                  element: <EmployeeSoftwareDetailsPage />,
                  handle: {
                    resource: true,
                    meta: () => {
                      return { title: i18n.t('software_management.employee_software.detail.title') }
                    },
                  },
                },
              ],
            },
          ],
        },
        {
          path: navigation.finance.spending.software.employees.path,
          element: <EmployeeSoftwarePage />,
          handle: {
            meta: () => {
              return {
                illustration: 'group',
                title: i18n.t('software_management.navbar.employees'),
                description: i18n.t('software_management.employee_software.table.description'),
              }
            },
          },
          children: [
            {
              index: true,
              element: <EmployeeSoftwareListPage />,
            },
            {
              path: navigation.finance.spending.software.employees.employeeId.path,
              element: <EmployeeSoftwareDetailsPage />,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('software_management.employee_software.detail.title'),
                  }
                },
              },
            },
          ],
        },
      ],
    },
  ]
})

export default softwareManagementSubModuleRoutes
